import { useCallback, useMemo } from 'react';

export default function usePermissions() {
  const getUserData = () => {
    try {
      const data = localStorage.getItem('user-data');
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  };

  const user = getUserData();
  const userPermissions = useMemo(() => user?.permissions || [], [user?.permissions]);
  const roleName = user?.roleName || '';
  const branch = user?.branch || '';
  const isSuperAdmin = roleName.toLowerCase() === 'super admin';

  const branchVal = (branch || '').toLowerCase().trim();
  const isUbdBranch = branchVal === 'ubd';
  const isUhplBranch = branchVal === 'uhpl' || branchVal === 'ubhl';
  const isBlankBranch = !branchVal;
  const isAdminBranch = branchVal === 'admin' || isBlankBranch;
  const isTadaAllowed = isUbdBranch;

  const isRouteAllowed = useCallback(
    (href, permissionName) => {
      // TA/DA Module: strictly visible ONLY for UBD or admin branch
      if (href === '/tada' || href === '/ta-da') {
        if (!isTadaAllowed) return false;
        if (isSuperAdmin) return true;
        if (!permissionName) return true;
        return userPermissions.includes(permissionName);
      }

      // Case 1: UBD branch -> Show all EXCEPT Site (/site) and Site Department (/site-department)
      if (isUbdBranch) {
        if (href === '/site' || href === '/site-department') return false;
        if (isSuperAdmin) return true;
        if (!permissionName) return true;
        return userPermissions.includes(permissionName);
      }

      // Case 2: UHPL branch -> Show ONLY Dashboard, Employee, Attendance, Master (Department, Designation, Site, Site Department), User Management
      if (isUhplBranch) {
        const allowedHrefs = [
          '/dashboard',
          '/employees',
          '/attendance',
          '/department',
          '/site',
          '/site-department',
          '/designation',
          '/users',
          '/roles',
          '/permissions',
        ];
        if (href && !allowedHrefs.includes(href)) {
          return false;
        }
        if (isSuperAdmin) return true;
        if (!permissionName) return true;
        return userPermissions.includes(permissionName);
      }

      // Case 3: Blank / Empty branch -> Show all modules (including Site)
      if (isBlankBranch) {
        if (isSuperAdmin) return true;
        if (!permissionName) return true;
        return userPermissions.includes(permissionName);
      }

      // Fallback for any other custom branch
      if (isSuperAdmin) return true;
      if (!permissionName) return true;
      return userPermissions.includes(permissionName);
    },
    [isTadaAllowed, isUbdBranch, isUhplBranch, isBlankBranch, isSuperAdmin, userPermissions]
  );

  const hasPermission = useCallback(
    (permissionName) => {
      if (!permissionName) return true;
      if (isSuperAdmin) return true;
      return userPermissions.includes(permissionName);
    },
    [isSuperAdmin, userPermissions]
  );

  const canCreatePlan = useMemo(() => {
    if (isUhplBranch) return false;
    if (isSuperAdmin) return true;
    return userPermissions.includes('pjp.create');
  }, [isUhplBranch, isSuperAdmin, userPermissions]);

  const canUpdatePlan = useMemo(() => {
    if (isUhplBranch) return false;
    if (isSuperAdmin) return true;
    return userPermissions.includes('pjp.update');
  }, [isUhplBranch, isSuperAdmin, userPermissions]);

  const canDeletePlan = useMemo(() => {
    if (isUhplBranch) return false;
    if (isSuperAdmin) return true;
    return userPermissions.includes('pjp.delete');
  }, [isUhplBranch, isSuperAdmin, userPermissions]);

  return {
    user,
    roleName,
    branch,
    isSuperAdmin,
    isUbdBranch,
    isUhplBranch,
    isBlankBranch,
    isAdminBranch,
    isTadaAllowed,
    userPermissions,
    hasPermission,
    isRouteAllowed,
    canCreatePlan,
    canUpdatePlan,
    canDeletePlan,
  };
}
